'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/contexts/ToastContext';

interface Review {
  id: string;
  template_id: string;
  user_id: string;
  rating: number;
  review_text: string;
  is_verified_purchaser: boolean;
  is_approved: boolean;
  helpful_count: number;
  not_helpful_count: number;
  created_at: string;
  author?: { username: string; avatar_url: string | null };
  user_vote?: boolean | null;
}

type SortOption = 'helpful' | 'recent' | 'highest' | 'lowest';

interface Props {
  templateId: string;
  userId: string;
}

export default function TemplateReviews({ templateId, userId }: Props) {
  const { showSuccess, showError } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortOption>('helpful');
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    loadReviews();
    checkPurchase();
  }, [templateId, userId]);

  const loadReviews = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('template_reviews')
      .select('*, author:community_users!inner(username, avatar_url)')
      .eq('template_id', templateId)
      .eq('is_approved', true);

    if (data) {
      // Load user votes
      const reviewIds = data.map((r: any) => r.id);
      let voteMap: Record<string, boolean | null> = {};
      if (reviewIds.length > 0) {
        const { data: votes } = await supabase
          .from('template_review_votes')
          .select('review_id, is_helpful')
          .eq('user_id', userId)
          .in('review_id', reviewIds);
        (votes || []).forEach((v: any) => { voteMap[v.review_id] = v.is_helpful; });
      }

      const enriched = data.map((r: any) => ({
        ...r,
        author: r.author,
        user_vote: voteMap[r.id] ?? null,
      }));

      setReviews(enriched);
      setHasReviewed(data.some((r: any) => r.user_id === userId));
    }
    setLoading(false);
  };

  const checkPurchase = async () => {
    const { data } = await supabase
      .from('purchases')
      .select('id')
      .eq('buyer_id', userId)
      .eq('template_id', templateId)
      .eq('status', 'paid')
      .maybeSingle();
    setHasPurchased(!!data);
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) return;
    setSubmitting(true);
    const { error } = await supabase.from('template_reviews').insert({
      template_id: templateId,
      user_id: userId,
      rating,
      review_text: reviewText.trim(),
      is_verified_purchaser: hasPurchased,
    });
    if (error) {
      showError(error.message || 'Failed to submit review');
    } else {
      showSuccess('Review submitted! It will appear after moderation.');
      setShowForm(false);
      setReviewText('');
      setRating(5);
      await loadReviews();
    }
    setSubmitting(false);
  };

  const handleVote = async (reviewId: string, isHelpful: boolean) => {
    const review = reviews.find((r) => r.id === reviewId);
    if (!review) return;

    if (review.user_vote === isHelpful) {
      // Remove vote
      await supabase.from('template_review_votes').delete().eq('review_id', reviewId).eq('user_id', userId);
      const delta = isHelpful ? -1 : 0;
      const notDelta = isHelpful ? 0 : -1;
      await supabase.from('template_reviews').update({
        helpful_count: Math.max(0, review.helpful_count + delta),
        not_helpful_count: Math.max(0, review.not_helpful_count + notDelta),
      }).eq('id', reviewId);
    } else {
      // Upsert vote
      await supabase.from('template_review_votes').upsert({ review_id: reviewId, user_id: userId, is_helpful: isHelpful }, { onConflict: 'review_id,user_id' });
      const helpfulDelta = isHelpful ? 1 : (review.user_vote === true ? -1 : 0);
      const notHelpfulDelta = !isHelpful ? 1 : (review.user_vote === false ? -1 : 0);
      await supabase.from('template_reviews').update({
        helpful_count: Math.max(0, review.helpful_count + helpfulDelta),
        not_helpful_count: Math.max(0, review.not_helpful_count + notHelpfulDelta),
      }).eq('id', reviewId);
    }
    await loadReviews();
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    if (sort === 'helpful') return b.helpful_count - a.helpful_count;
    if (sort === 'recent') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    if (sort === 'highest') return b.rating - a.rating;
    if (sort === 'lowest') return a.rating - b.rating;
    return 0;
  });

  const avgRating = reviews.length > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>Community Reviews</h3>
          {reviews.length > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Icon key={s} name="StarIcon" size={14} variant="solid" style={{ color: s <= Math.round(avgRating) ? '#f59e0b' : 'var(--border)' }} />
                ))}
              </div>
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{avgRating.toFixed(1)}</span>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>({reviews.length} reviews)</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="text-xs px-3 py-1.5 rounded-lg"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-secondary)', outline: 'none' }}
          >
            <option value="helpful">Most Helpful</option>
            <option value="recent">Most Recent</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
          {!hasReviewed && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary text-xs flex items-center gap-1.5">
              <Icon name="PencilIcon" size={12} />
              Write Review
            </button>
          )}
        </div>
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="p-5 rounded-xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--text-primary)' }}>Write a Review</h4>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Your Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(s)}
                    className="transition-transform hover:scale-110"
                  >
                    <Icon
                      name="StarIcon"
                      size={24}
                      variant="solid"
                      style={{ color: s <= (hoverRating || rating) ? '#f59e0b' : 'var(--border)' }}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Your Review</label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience with this template..."
                rows={4}
                className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)', outline: 'none' }}
              />
            </div>
            <div className="flex gap-3">
              <button onClick={handleSubmitReview} disabled={submitting || !reviewText.trim()} className="btn-primary text-sm">
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: 'var(--bg-secondary)' }} />)}
        </div>
      ) : sortedReviews.length === 0 ? (
        <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
          <Icon name="StarIcon" size={32} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm">No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedReviews.map((review) => (
            <div key={review.id} className="p-4 rounded-xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden"
                    style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}
                  >
                    {review.author?.avatar_url ? (
                      <img src={review.author.avatar_url} alt={review.author.username} className="w-full h-full object-cover" />
                    ) : (
                      review.author?.username?.[0]?.toUpperCase() || '?'
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{review.author?.username || 'Anonymous'}</span>
                      {review.is_verified_purchaser && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#10b98122', color: '#10b981' }}>✓ Verified Purchaser</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Icon key={s} name="StarIcon" size={12} variant="solid" style={{ color: s <= review.rating ? '#f59e0b' : 'var(--border)' }} />
                      ))}
                    </div>
                  </div>
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                  {new Date(review.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{review.review_text}</p>
              {/* Helpful voting */}
              <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Was this helpful?</span>
                <button
                  onClick={() => handleVote(review.id, true)}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-all"
                  style={{
                    background: review.user_vote === true ? '#10b98122' : 'var(--bg-secondary)',
                    color: review.user_vote === true ? '#10b981' : 'var(--text-secondary)',
                  }}
                >
                  <Icon name="HandThumbUpIcon" size={12} />
                  {review.helpful_count}
                </button>
                <button
                  onClick={() => handleVote(review.id, false)}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-all"
                  style={{
                    background: review.user_vote === false ? '#ef444422' : 'var(--bg-secondary)',
                    color: review.user_vote === false ? '#ef4444' : 'var(--text-secondary)',
                  }}
                >
                  <Icon name="HandThumbDownIcon" size={12} />
                  {review.not_helpful_count}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
